/**
 * Everything a client's tabs share: the metric catalogue (with the client's
 * custom metrics and views as extra sources) and templates, the client's
 * documents, and the agency's branding.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { customApi, type CustomMetric, type CustomView } from '../custom/custom-api.ts';
import { withCustom } from '../custom/with-custom.ts';

import type { Branding } from '../portal/portal-api.ts';
import { agencyApi } from '../reports/agency-api.ts';
import {
  reportsApi,
  type Catalogue,
  type ReportDocument,
  type Template,
} from '../reports/reports-api.ts';

export interface ClientWorkspace {
  catalogue: Catalogue | null;
  templates: Template[];
  documents: ReportDocument[] | null;
  branding: Branding;
  reloadDocuments: () => void;
  /** After a custom metric or view changes. */
  reloadCustom: () => void;
  setBranding: (branding: Branding) => void;
}

export function useClientWorkspace(
  organisationId: string,
  organisationName: string,
  clientId: string,
): ClientWorkspace {
  const [base, setBase] = useState<Catalogue | null>(null);
  const [metrics, setMetrics] = useState<CustomMetric[]>([]);
  const [views, setViews] = useState<CustomView[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [documents, setDocuments] = useState<ReportDocument[] | null>(null);
  const [branding, setBranding] = useState<Branding>({
    display_name: organisationName,
    accent: null,
    logo: null,
  });

  useEffect(() => {
    reportsApi
      .catalogue(organisationId)
      .then((data) => {
        setBase(data.catalogue);
        setTemplates(data.templates);
      })
      .catch(() => setBase(null));
    agencyApi
      .branding(organisationId)
      .then(setBranding)
      .catch(() => undefined);
  }, [organisationId]);

  const reloadDocuments = useCallback(() => {
    reportsApi
      .documents(organisationId, clientId)
      .then(setDocuments)
      .catch(() => setDocuments([]));
  }, [organisationId, clientId]);

  useEffect(() => {
    setDocuments(null);
    reloadDocuments();
  }, [reloadDocuments]);

  const reloadCustom = useCallback(() => {
    customApi
      .metrics(organisationId, clientId)
      .then(setMetrics)
      .catch(() => setMetrics([]));
    customApi
      .views(organisationId, clientId)
      .then(setViews)
      .catch(() => setViews([]));
  }, [organisationId, clientId]);
  useEffect(reloadCustom, [reloadCustom]);

  const catalogue = useMemo(
    () => (base ? withCustom(base, metrics, views) : null),
    [base, metrics, views],
  );
  return { catalogue, templates, documents, branding, reloadDocuments, reloadCustom, setBranding };
}
